/** Per-day breakdown of a plan, sliced at its dayBreaks (CONTROL-POINT
 * indices — types.ts's Plan.dayBreaks doc). Pure — no rendering, no I/O;
 * PlanBar's future day-by-day wave reads this the same way it already
 * reads PlanStats. Reuses geo.ts's haversineM/gainLossM/etaSeconds rather
 * than re-deriving distance/elevation math — phone parity lives there,
 * this file only ever slices an already-computed sample set by distance. */

import { gainLossM, etaSeconds, haversineM } from "./geo";
import type { ElevationSample, LonLat, Plan } from "./types";

/** Cumulative distance (meters) along the flattened route geometry —
 * planGeometry's exact traversal order (points[i], then legs[i].via, for
 * each i in turn) — at each CONTROL point. controlPointDistM(plan)[k] is
 * the distance from the route's start to points[k]; the returned array's
 * length always matches plan.points, regardless of how much via geometry
 * sits between control points. */
export function controlPointDistM(plan: Pick<Plan, "points" | "legs">): number[] {
  const out: number[] = [];
  let cum = 0;
  let prev: LonLat | null = null;
  for (let i = 0; i < plan.points.length; i++) {
    if (prev) cum += haversineM(prev, plan.points[i]);
    prev = plan.points[i];
    out.push(cum);

    const leg = plan.legs[i];
    if (leg && i < plan.points.length - 1) {
      for (const via of leg.via) {
        cum += haversineM(prev, via);
        prev = via;
      }
    }
  }
  return out;
}

export interface DayStat {
  day: number;
  startM: number;
  endM: number;
  distanceM: number;
  gainM: number | null;
  lossM: number | null;
  etaSeconds: number | null;
}

/** One entry per day: day 1 runs from the route's start to the first
 * dayBreaks control point, day 2 from there to the second, … the last day
 * to the route's end. dayBreaks.length + 1 entries when the plan has ≥2
 * points (a single point or empty plan has no route to slice, so []).
 * distanceM is a pure function of the control points themselves and is
 * always real; the elevation-derived fields (gainM/lossM/etaSeconds) come
 * from whichever `samples` fall within [startM, endM] — fewer than 2
 * there can't derive a delta, so those three are null while distanceM
 * still reports. Trusts plan.dayBreaks is already sorted/interior (every
 * producer in plannerCore.ts maintains that invariant; loadPlan sanitizes
 * anything that didn't come from here). */
export function dayStats(plan: Plan, samples: ElevationSample[]): DayStat[] {
  if (plan.points.length < 2) return [];

  const cum = controlPointDistM(plan);
  const totalM = cum[cum.length - 1];
  const breaks = plan.dayBreaks ?? [];
  const bounds = [0, ...breaks.map((i) => cum[i]), totalM];

  return Array.from({ length: bounds.length - 1 }, (_, day) => {
    const startM = bounds[day];
    const endM = bounds[day + 1];
    const daySamples = samples.filter((s) => s.distM >= startM && s.distM <= endM);
    const enough = daySamples.length >= 2;
    const { gainM, lossM } = enough ? gainLossM(daySamples) : { gainM: null, lossM: null };
    return {
      day: day + 1,
      startM,
      endM,
      distanceM: endM - startM,
      gainM,
      lossM,
      etaSeconds: enough ? etaSeconds(daySamples) : null,
    };
  });
}
